import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from './ui/button';
import { ProfileWizard } from './ProfileWizard';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { Menu, X, User, LogOut, LayoutDashboard, Shield, MapPin, Calendar, MessageSquare, Map, FileText, Mail, Circle, ChevronDown, Building2, Settings, Navigation, Star, HelpCircle, Pencil, Moon, Compass, Rss, Flame, Route, Trophy, Users, Plus, Store, Search, Inbox, Heart, Globe, Film, Bookmark, Bike, Sparkles } from 'lucide-react';
import { useState, useEffect } from 'react';
import axios from 'axios';
import LanguageSelector from './LanguageSelector';
import NotificationBell from './NotificationBell';
import { InviteDialog } from './InviteDialog';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Navbar = () => {
  const { user, logout } = useAuth();
  const { t, language, setLanguage } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [showProfileWizard, setShowProfileWizard] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [ticketUnreadCount, setTicketUnreadCount] = useState(0);
  const [pendingRequestsCount, setPendingRequestsCount] = useState(0);
  const [myClubs, setMyClubs] = useState([]);
  const [myEventsCount, setMyEventsCount] = useState(0);

  useEffect(() => {
    if (user) {
      fetchUnreadCount();
      fetchTicketUnreadCount();
      fetchPendingRequests();
      fetchMyClubs();
      fetchMyEventsCount();
      const interval = setInterval(() => { fetchUnreadCount(); fetchTicketUnreadCount(); fetchPendingRequests(); fetchMyEventsCount(); }, 30000);
      return () => clearInterval(interval);
    }
  }, [user]);

  const fetchUnreadCount = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      const response = await axios.get(`${API_URL}/messages/unread-count`);
      setUnreadCount(response.data.count);
    } catch (error) {
      console.error('Failed to fetch unread count');
    }
  };

  const fetchTicketUnreadCount = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      const response = await axios.get(`${API_URL}/my/messages/unread-count`);
      setTicketUnreadCount(response.data.count);
    } catch {}
  };

  const fetchPendingRequests = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      const response = await axios.get(`${API_URL}/clubs/pending-requests-count`);
      setPendingRequestsCount(response.data.count);
    } catch {}
  };

  const fetchMyClubs = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      const response = await axios.get(`${API_URL}/clubs/my-clubs`);
      setMyClubs(response.data || []);
    } catch {}
  };

  const fetchMyEventsCount = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      const response = await axios.get(`${API_URL}/users/my-events/count`);
      setMyEventsCount(response.data.count || 0);
    } catch {}
  };

  const handleMyClubClick = () => {
    if (myClubs.length === 1) {
      navigate(`/klubber/${myClubs[0].id}`);
    } else if (myClubs.length > 1) {
      navigate(`/klubber/${myClubs[0].id}`);
    } else {
      navigate('/opret-klub');
    }
  };


  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const mainNavLinks = [
    { href: '/klubber', labelKey: 'nav.findVisit', icon: MapPin },
    { href: '/kort', labelKey: 'nav.map', icon: Map },
    { href: '/plan-tur', labelKey: 'nav.planTrip', icon: Route },
    { href: '/klubber?overnight=true', labelKey: 'nav.findAccommodation', icon: Moon },
    { href: '/regler', labelKey: 'nav.rules', icon: FileText },
    { href: '/kontakt', labelKey: 'nav.contact', icon: Mail },
    ...(user?.user_type === 'admin' ? [{ href: '/admin', labelKey: 'nav.adminPanel', icon: Shield }] : []),
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 h-14" style={{ paddingTop: 'env(safe-area-inset-top)' }} data-testid="navbar">
      <div 
        className="absolute inset-0 backdrop-blur-sm"
        style={{ background: 'linear-gradient(to bottom, rgba(6, 78, 59, 0.95) 0%, rgba(6, 78, 59, 0.70) 50%, rgba(6, 78, 59, 0) 100%)' }}
      />
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-14">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 group" data-testid="logo-link">
            <img 
              src="/assets/baremc-logo-transparent.png" 
              alt="BareMC" 
              className="h-9 w-auto transition-transform group-hover:scale-105"
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-4 flex-shrink min-w-0">
            {mainNavLinks.map((link) => (
              <Link
                key={link.href}
                to={link.href}
                data-testid={`nav-${(link.labelKey || link.label || '').split('.').pop()}`}
                className={`flex items-center gap-1.5 text-xs font-medium tracking-wide transition-colors whitespace-nowrap ${
                  isActive(link.href) ? 'text-white' : 'text-white/60 hover:text-white'
                }`}
              >
                <link.icon className="w-3.5 h-3.5 flex-shrink-0" />
                {link.labelKey ? t(link.labelKey) : link.label}
              </Link>
            ))}
          </div>

          {/* Right side */}
          <div className="flex items-center gap-2">
            {user && <NotificationBell />}
            
            {/* Direct admin button - outside all dropdowns */}
            {user?.is_site_admin && (
              <Link 
                to="/admin"
                className="flex items-center gap-1 px-2.5 py-1.5 rounded-full bg-red-500/20 border border-red-500/30 text-red-300 text-xs font-semibold hover:bg-red-500/30 transition-all whitespace-nowrap"
                data-testid="direct-admin-btn"
              >
                <Shield className="w-3 h-3 flex-shrink-0" />
                <span className="hidden sm:inline">Speciel Indstilling</span>
              </Link>
            )}
            
            {user ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button 
                    className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white/10 border border-white/10 hover:border-white/20 transition-all text-xs"
                    data-testid="user-menu-trigger"
                  >
                    <Circle className="w-2 h-2 text-green-400 fill-green-400" />
                    <div className="w-6 h-6 rounded-full bg-gradient-to-br from-emerald-300 to-emerald-500 flex items-center justify-center">
                      {user.user_type === 'club' ? (
                        <Building2 className="w-3 h-3 text-white" />
                      ) : (
                        <User className="w-3 h-3 text-white" />
                      )}
                    </div>
                    <span className="hidden sm:inline text-white/90 font-medium max-w-[80px] truncate">{user.name}</span>
                    {user.user_type === 'admin' && <Star className="w-3 h-3 text-red-500 fill-red-500 flex-shrink-0" data-testid="logged-in-star" />}
                    <ChevronDown className="w-3 h-3 text-white/70 hidden sm:block" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-52 bg-zinc-900 border-zinc-700 shadow-xl rounded-xl">
                  <div className="px-3 py-2.5 border-b border-white/10">
                    <div className="font-semibold text-sm text-white">{user.name}</div>
                    <div className="text-zinc-400 text-xs truncate">{user.email}</div>
                  </div>
                  
                  <DropdownMenuItem onSelect={() => navigate('/konto')} className="cursor-pointer py-2 text-sm text-zinc-300 hover:text-white focus:text-white" data-testid="account-link">
                    <User className="w-3.5 h-3.5 mr-2 text-zinc-400" />
                    {t('nav.myAccount')}
                  </DropdownMenuItem>
                  
                  <DropdownMenuItem onSelect={() => navigate('/profil')} className="cursor-pointer py-2 text-sm text-zinc-300 hover:text-white focus:text-white" data-testid="profile-link">
                    <User className="w-3.5 h-3.5 mr-2 text-emerald-400" />
                    {t('account.myProfile')}
                  </DropdownMenuItem>
                  
                  {user && (
                    <DropdownMenuItem onSelect={() => handleMyClubClick()} className="cursor-pointer py-2 text-sm text-zinc-300 hover:text-white focus:text-white" data-testid="my-club-link">
                      <div className="relative mr-2">
                        <Building2 className="w-3.5 h-3.5 text-emerald-400" />
                        {pendingRequestsCount > 0 && (
                          <span className="absolute -top-1.5 -right-1.5 bg-red-500 text-white text-[8px] font-bold rounded-full w-3.5 h-3.5 flex items-center justify-center">
                            {pendingRequestsCount > 9 ? '9+' : pendingRequestsCount}
                          </span>
                        )}
                      </div>
                      {myClubs.length > 0 ? 'Min Klub' : 'Opret Klub'}
                    </DropdownMenuItem>
                  )}
                  
                  <DropdownMenuItem onSelect={() => navigate('/farli')} className="cursor-pointer py-2 text-sm text-zinc-300 hover:text-white focus:text-white" data-testid="farli-link">
                    <div className="w-4 h-4 rounded-full border border-white/20 overflow-hidden mr-2 flex-shrink-0">
                      <img 
                        src="https://customer-assets.emergentagent.com/job_mc-community-1/artifacts/0iwvudcy_905DB496-6C04-454A-A77D-D1FDDD2ED3CA.jpeg" 
                        alt="Farli" 
                        className="w-full h-full object-cover"
                      />
                    </div>
                    {t('farli.settings')}
                  </DropdownMenuItem>
                  
                  <DropdownMenuItem onSelect={() => navigate('/favoritter')} className="cursor-pointer py-2 text-sm text-zinc-300 hover:text-white focus:text-white" data-testid="favorites-link">
                    <Heart className="w-3.5 h-3.5 mr-2 text-red-400" />
                    Mine Favoritter
                  </DropdownMenuItem>
                  
                  {(user.user_type === 'club' || user.user_type === 'admin') && (
                    <>
                      <DropdownMenuItem onSelect={() => navigate('/beskeder')} className="cursor-pointer py-2 text-sm text-zinc-300 hover:text-white focus:text-white" data-testid="messages-link">
                        <MessageSquare className="w-3.5 h-3.5 mr-2 text-zinc-400" />
                        {t('nav.messages')}
                        {unreadCount > 0 && (
                          <span className="ml-auto bg-red-500 text-white text-[10px] px-1.5 py-0.5 rounded-full font-bold">
                            {unreadCount}
                          </span>
                        )}
                      </DropdownMenuItem>
                    </>
                  )}
                  
                  {user.user_type === 'admin' && (
                    <div 
                      role="menuitem"
                      onClick={(e) => { e.preventDefault(); e.stopPropagation(); setTimeout(() => navigate('/admin'), 50); }}
                      className="cursor-pointer py-2 text-sm bg-amber-500/15 text-amber-400 rounded-md my-0.5 mx-1 flex items-center px-2" 
                      data-testid="admin-link"
                    >
                      <Star className="w-3.5 h-3.5 mr-2 fill-amber-400" />
                      {t('nav.adminPanel')}
                    </div>
                  )}
                  
                  <DropdownMenuSeparator className="bg-white/10" />
                  <DropdownMenuItem 
                    onSelect={handleLogout}
                    className="cursor-pointer text-red-400 py-2 text-sm"
                    data-testid="logout-btn"
                  >
                    <LogOut className="w-3.5 h-3.5 mr-2" />
                    {t('nav.logout')}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <div className="hidden sm:flex items-center gap-2">
                <Link to="/login">
                  <Button 
                    variant="ghost" 
                    className="text-white/60 hover:text-white text-xs h-8 px-3"
                    data-testid="login-btn"
                  >
                    {t('nav.login')}
                  </Button>
                </Link>
                <Link to="/register">
                  <button 
                    className="h-8 px-4 rounded-full text-xs font-semibold bg-emerald-400 text-zinc-900 hover:bg-emerald-300 hover:text-zinc-900 transition-colors"
                    data-testid="register-btn"
                  >
                    {t('nav.createAccount')}
                  </button>
                </Link>
              </div>
            )}

            {/* Language selector — always visible (mobile + desktop) so the
                rider can switch language at any time without digging into the
                hamburger menu. */}
            <LanguageSelector compact data-testid="language-flags" className="flex" />

            {/* Hamburger */}
            <DropdownMenu open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
              <DropdownMenuTrigger asChild>
                <button
                  className="relative flex items-center gap-1.5 px-2 py-1.5 text-white/60 hover:text-white min-h-[36px] touch-manipulation rounded-lg hover:bg-white/10 transition-all"
                  data-testid="mobile-menu-btn"
                >
                  <span className="font-medium text-xs hidden sm:inline">{t('nav.menu')}</span>
                  <div className="relative">
                    {mobileMenuOpen ? <X className="w-5 h-5" /> : <Settings className="w-5 h-5" />}
                    {!mobileMenuOpen && user && unreadCount > 0 && (
                      <span className="absolute -top-1.5 -right-1.5 bg-red-500 text-white text-[9px] font-bold rounded-full w-4 h-4 flex items-center justify-center">
                        {unreadCount > 9 ? '9+' : unreadCount}
                      </span>
                    )}
                  </div>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent 
                align="end" 
                className="w-60 bg-[#0a2f1f]/95 backdrop-blur-xl border-white/10 overflow-hidden rounded-xl shadow-2xl overflow-y-auto"
                style={{ maxHeight: '65vh' }}
              >
                <div className="px-3 py-1.5 border-b border-white/10 flex items-center gap-2">
                  <img loading="lazy" src="/assets/baremc-logo-transparent.png" alt="BareMC" className="h-5 w-auto" />
                  <span className="text-white/80 text-xs font-medium">{t('nav.menu')}</span>
                </div>

                <div className="py-0.5">
                  {/* ═══ MIT BAREMC — Top section for logged-in users ═══ */}
                  {user && (
                    <>
                      <div className="px-3 py-1">
                        <span className="text-emerald-400 text-[9px] font-bold uppercase tracking-wider">Mit BareMC</span>
                      </div>

                      {/* MINE TURE — top position, amber accent so it stands out */}
                      <DropdownMenuItem
                        onSelect={() => { navigate('/mine-ture'); setMobileMenuOpen(false); }}
                        className="cursor-pointer py-2.5 px-3 mx-1 my-0.5 rounded-xl transition-all text-sm touch-manipulation font-black text-white hover:bg-amber-400/10 uppercase tracking-wide"
                        style={{ background: isActive('/mine-ture') ? 'rgba(245,158,11,0.14)' : 'rgba(245,158,11,0.04)', border: '1px solid rgba(245,158,11,0.35)' }}
                        data-testid="nav-my-rides-top"
                      >
                        <Route className="w-4 h-4 mr-2 text-amber-400" />
                        MINE TURE
                      </DropdownMenuItem>

                      {/* Min Profil — prominent */}
                      <DropdownMenuItem 
                        onSelect={() => { navigate('/profil'); setMobileMenuOpen(false); }}
                        className="cursor-pointer py-2.5 px-3 mx-1 my-0.5 rounded-xl transition-all text-sm touch-manipulation font-bold text-white hover:bg-purple-400/10"
                        style={{ background: isActive('/profil') ? 'rgba(168,85,247,0.12)' : 'rgba(255,255,255,0.04)', border: '1px solid rgba(168,85,247,0.2)' }}
                        data-testid="nav-my-profile"
                      >
                        <User className="w-4 h-4 mr-2 text-purple-400" />
                        Min Profil
                      </DropdownMenuItem>

                      {/* Min Klub — prominent */}
                      <DropdownMenuItem 
                        onSelect={() => { handleMyClubClick(); setMobileMenuOpen(false); }}
                        className="cursor-pointer py-2.5 px-3 mx-1 my-0.5 rounded-xl transition-all text-sm touch-manipulation font-bold text-white hover:bg-emerald-400/10"
                        style={{ background: isActive('/klubber/') || isActive('/dashboard') ? 'rgba(16,185,129,0.12)' : 'rgba(255,255,255,0.04)', border: '1px solid rgba(16,185,129,0.2)' }}
                        data-testid="nav-my-club"
                      >
                        <div className="relative mr-2">
                          <Building2 className="w-4 h-4 text-emerald-400" />
                          {pendingRequestsCount > 0 && (
                            <span className="absolute -top-1.5 -right-1.5 bg-red-500 text-white text-[8px] font-bold rounded-full w-3.5 h-3.5 flex items-center justify-center animate-pulse">
                              {pendingRequestsCount > 9 ? '9+' : pendingRequestsCount}
                            </span>
                          )}
                        </div>
                        {myClubs.length > 0 ? 'Min Klub' : 'Opret Klub'}
                      </DropdownMenuItem>

                      {/* Eventkalender — prominent */}
                      <DropdownMenuItem 
                        onSelect={() => { navigate('/events'); setMobileMenuOpen(false); }}
                        className="cursor-pointer py-2.5 px-3 mx-1 my-0.5 rounded-xl transition-all text-sm touch-manipulation font-bold text-white hover:bg-amber-400/10"
                        style={{ background: isActive('/events') ? 'rgba(251,191,36,0.12)' : 'rgba(255,255,255,0.04)', border: '1px solid rgba(251,191,36,0.2)' }}
                        data-testid="nav-events-calendar"
                      >
                        <Calendar className="w-4 h-4 mr-2 text-amber-400" />
                        Eventkalender
                        {myEventsCount > 0 && (
                          <span className="ml-auto text-[10px] font-bold text-amber-400 bg-amber-500/15 px-1.5 py-0.5 rounded-full">{myEventsCount}</span>
                        )}
                      </DropdownMenuItem>

                      {/* Inviter venner — WhatsApp/SMS share */}
                      <DropdownMenuItem
                        onSelect={(e) => { e.preventDefault?.(); setInviteOpen(true); setMobileMenuOpen(false); }}
                        className="cursor-pointer py-2.5 px-3 mx-1 my-0.5 rounded-xl transition-all text-sm touch-manipulation font-bold text-white hover:bg-emerald-400/10"
                        style={{ background: 'rgba(16,185,129,0.08)', border: '1px solid rgba(16,185,129,0.2)' }}
                        data-testid="nav-invite-friends"
                      >
                        <MessageSquare className="w-4 h-4 mr-2 text-emerald-400" /> {t('gen.4d72e7583d')}
                      </DropdownMenuItem>
                    </>
                  )}

                  {/* ═══ FEED (prominent, top of Mit BareMC) ═══ */}
                  {user && (
                    <>
                      <DropdownMenuItem
                        onSelect={() => { navigate('/feed'); setMobileMenuOpen(false); }}
                        className="cursor-pointer py-2 px-3 mx-1 my-0.5 rounded-xl transition-all text-sm touch-manipulation font-bold text-white hover:bg-emerald-400/10"
                        style={{ background: isActive('/feed') ? 'rgba(16,185,129,0.1)' : 'rgba(255,255,255,0.03)', border: '1px solid rgba(16,185,129,0.15)' }}
                        data-testid="nav-feed"
                      >
                        <Rss className="w-4 h-4 mr-2 text-emerald-400" />
                        Feed
                      </DropdownMenuItem>
                      <DropdownMenuSeparator className="bg-white/10 my-0.5" />
                    </>
                  )}

                  {/* ═══ LEADERBOARD ═══ */}
                  <DropdownMenuSeparator className="bg-white/10 my-0.5" />
                  <DropdownMenuItem
                    onSelect={() => { navigate('/leaderboard'); setMobileMenuOpen(false); }}
                    className="cursor-pointer py-2 px-3 mx-1 my-0.5 rounded-xl transition-all text-sm touch-manipulation font-bold text-white hover:bg-amber-400/10"
                    style={{ background: isActive('/leaderboard') ? 'rgba(251,191,36,0.1)' : 'rgba(255,255,255,0.03)', border: '1px solid rgba(251,191,36,0.15)' }}
                    data-testid="nav-leaderboard"
                  >
                    <Trophy className="w-4 h-4 mr-2 text-amber-400" />
                    Leaderboard
                  </DropdownMenuItem>

                  {/* ═══ OPRET ═══ */}
                  <DropdownMenuSeparator className="bg-white/10 my-0.5" />
                  <div className="px-3 py-1">
                    <span className="text-emerald-400 text-[9px] font-bold uppercase tracking-wider flex items-center gap-1">
                      <Plus className="w-2.5 h-2.5" /> {t('nav.create') || 'Opret'}
                    </span>
                  </div>
                  {/* Bruger */}
                  <DropdownMenuItem
                    onSelect={() => { navigate('/register?type=user'); setMobileMenuOpen(false); }}
                    className="cursor-pointer py-1.5 px-3 mx-1 rounded-lg transition-all text-xs touch-manipulation text-white/70 hover:text-white hover:bg-white/5"
                    data-testid="nav-create-user"
                  >
                    <User className="w-3.5 h-3.5 mr-2 text-blue-400" />
                    Bruger
                  </DropdownMenuItem>
                  {/* MC-klub */}
                  <DropdownMenuItem
                    onSelect={() => { navigate('/register?type=club'); setMobileMenuOpen(false); }}
                    className="cursor-pointer py-1.5 px-3 mx-1 rounded-lg transition-all text-xs touch-manipulation text-white/70 hover:text-white hover:bg-white/5"
                    data-testid="nav-create-club"
                  >
                    <Bike className="w-3.5 h-3.5 mr-2 text-orange-400" />
                    MC-klub
                  </DropdownMenuItem>
                  {/* Andet — åbner wizard */}
                  <DropdownMenuItem
                    onSelect={() => { setMobileMenuOpen(false); setTimeout(() => setShowProfileWizard(true), 150); }}
                    className="cursor-pointer py-1.5 px-3 mx-1 rounded-lg transition-all text-xs touch-manipulation text-white/70 hover:text-white hover:bg-white/5"
                    data-testid="nav-create-andet"
                  >
                    <Sparkles className="w-3.5 h-3.5 mr-2 text-purple-400" />
                    Andet
                  </DropdownMenuItem>

                  {/* ═══ UDFORSK ═══ */}
                  <DropdownMenuSeparator className="bg-white/10 my-0.5" />
                  <div className="px-3 py-1">
                    <span className="text-emerald-400 text-[9px] font-bold uppercase tracking-wider">{t('nav.findVisit') ? 'Udforsk' : 'Explore'}</span>
                  </div>
                  {mainNavLinks.map((link) => (
                    <DropdownMenuItem 
                      key={link.href}
                      onSelect={() => { navigate(link.href); setMobileMenuOpen(false); }}
                      className={`cursor-pointer py-1.5 px-3 mx-1 rounded-lg transition-all text-xs touch-manipulation ${
                        isActive(link.href) ? 'text-white bg-white/10 font-medium' : 'text-white/70 hover:text-white hover:bg-white/5'
                      }`}
                    >
                      <link.icon className="w-3.5 h-3.5 mr-2" />
                      {link.labelKey ? t(link.labelKey) : link.label}
                    </DropdownMenuItem>
                  ))}
                  <DropdownMenuItem 
                    onSelect={() => { navigate('/discover'); setMobileMenuOpen(false); }}
                    className={`cursor-pointer py-1.5 px-3 mx-1 rounded-lg transition-all text-xs touch-manipulation ${
                      isActive('/discover') ? 'text-white bg-white/10 font-medium' : 'text-white/70 hover:text-white hover:bg-white/5'
                    }`}
                    data-testid="nav-discover"
                  >
                    <Compass className="w-3.5 h-3.5 mr-2 text-emerald-400" />
                    Discover
                  </DropdownMenuItem>

                  {/* ═══ EXTRA MIT BAREMC ITEMS ═══ */}
                  {user && (
                    <>
                      <DropdownMenuSeparator className="bg-white/10 my-0.5" />
                      <DropdownMenuItem 
                        onSelect={() => { navigate('/beskeder'); setMobileMenuOpen(false); }}
                        className={`cursor-pointer py-1.5 px-3 mx-1 rounded-lg transition-all text-xs touch-manipulation ${
                          isActive('/beskeder') || isActive('/mine-beskeder') ? 'text-white bg-white/10 font-medium' : 'text-white/70 hover:text-white hover:bg-white/5'
                        }`}
                        data-testid="nav-messages-combined"
                      >
                        <div className="relative mr-2">
                          <MessageSquare className="w-3.5 h-3.5 text-emerald-400" />
                          {(unreadCount + ticketUnreadCount) > 0 && (
                            <span className="absolute -top-1.5 -right-1.5 bg-red-500 text-white text-[8px] font-bold rounded-full w-3.5 h-3.5 flex items-center justify-center">
                              {(unreadCount + ticketUnreadCount) > 9 ? '9+' : (unreadCount + ticketUnreadCount)}
                            </span>
                          )}
                        </div>
                        {t('nav.messages')}
                      </DropdownMenuItem>
                      {/* Mine ture — user can find saved rides */}
                      <DropdownMenuItem
                        onSelect={() => { navigate('/mine-ture'); setMobileMenuOpen(false); }}
                        className={`cursor-pointer py-1.5 px-3 mx-1 rounded-lg transition-all text-xs touch-manipulation ${
                          isActive('/mine-ture') ? 'text-white bg-white/10 font-medium' : 'text-white/70 hover:text-white hover:bg-white/5'
                        }`}
                        data-testid="nav-my-rides"
                      >
                        <Route className="w-3.5 h-3.5 mr-2 text-[#00FF88]" />
                        Mine ture
                      </DropdownMenuItem>
                      {/* Bibliotek — gemte turvideoer + opslag */}
                      <DropdownMenuItem
                        onSelect={() => { navigate('/bibliotek'); setMobileMenuOpen(false); }}
                        className={`cursor-pointer py-1.5 px-3 mx-1 rounded-lg transition-all text-xs touch-manipulation ${
                          isActive('/bibliotek') ? 'text-white bg-white/10 font-medium' : 'text-white/70 hover:text-white hover:bg-white/5'
                        }`}
                        data-testid="nav-saved-library"
                      >
                        <Bookmark className="w-3.5 h-3.5 mr-2 text-emerald-400" />
                        Mit bibliotek
                      </DropdownMenuItem>
                      <DropdownMenuItem 
                        onSelect={() => { navigate('/nummerplade'); setMobileMenuOpen(false); }}
                        className={`cursor-pointer py-1.5 px-3 mx-1 rounded-lg transition-all text-xs touch-manipulation ${
                          isActive('/nummerplade') ? 'text-white bg-white/10 font-medium' : 'text-white/70 hover:text-white hover:bg-white/5'
                        }`}
                        data-testid="nav-plate-lookup"
                      >
                        <Search className="w-3.5 h-3.5 mr-2 text-violet-400" />
                        Nummerplade Opslag
                      </DropdownMenuItem>
                    </>
                  )}
                </div>

                {/* ═══ ADMIN (kun site admins) ═══ */}
                {user?.is_site_admin && (
                  <div className="border-t border-white/10 py-0.5 px-1">
                    <div className="px-3 py-1">
                      <span className="text-red-400 text-[9px] font-bold uppercase tracking-wider">Admin</span>
                    </div>
                    <div 
                      role="menuitem"
                      onClick={(e) => { e.preventDefault(); e.stopPropagation(); setMobileMenuOpen(false); setTimeout(() => navigate('/admin'), 50); }}
                      className="cursor-pointer py-1.5 px-3 rounded-lg text-white/70 hover:text-white hover:bg-white/5 text-xs touch-manipulation flex items-center" 
                      data-testid="nav-admin-panel"
                    >
                      <Shield className="w-3.5 h-3.5 mr-2 text-red-400" />
                      <span className="flex-1">Speciel Indstilling</span>
                      <Star className="w-2.5 h-2.5 text-red-500 fill-red-500 ml-1" />
                    </div>
                    <div 
                      role="menuitem"
                      onClick={(e) => { e.preventDefault(); e.stopPropagation(); setMobileMenuOpen(false); setTimeout(() => navigate('/admin/indhold'), 50); }}
                      className="cursor-pointer py-1.5 px-3 rounded-lg text-white/70 hover:text-white hover:bg-white/5 text-xs touch-manipulation flex items-center" 
                      data-testid="nav-admin-content"
                    >
                      <Pencil className="w-3.5 h-3.5 mr-2 text-red-400" />
                      <span className="flex-1">{t('admin.editOffers')}</span>
                      <Star className="w-2.5 h-2.5 text-red-500 fill-red-500 ml-1" />
                    </div>
                    <div 
                      role="menuitem"
                      onClick={(e) => { e.preventDefault(); e.stopPropagation(); setMobileMenuOpen(false); setTimeout(() => navigate('/dashboard'), 50); }}
                      className="cursor-pointer py-1.5 px-3 rounded-lg text-white/70 hover:text-white hover:bg-white/5 text-xs touch-manipulation flex items-center" 
                      data-testid="nav-admin-dashboard"
                    >
                      <LayoutDashboard className="w-3.5 h-3.5 mr-2 text-red-400" />
                      <span className="flex-1">Dashboard</span>
                      <Star className="w-2.5 h-2.5 text-red-500 fill-red-500 ml-1" />
                    </div>
                    {(user?.email === 'timmotto@gmail.com' || user?.email === 'werby@theothers.dk' || user?.is_site_admin) && (
                      <div 
                        role="menuitem"
                        onClick={(e) => { e.preventDefault(); e.stopPropagation(); setMobileMenuOpen(false); setTimeout(() => navigate('/admin?tab=inbox'), 50); }}
                        className="cursor-pointer py-1.5 px-3 rounded-lg text-white/70 hover:text-white hover:bg-white/5 text-xs touch-manipulation flex items-center" 
                        data-testid="nav-admin-inbox"
                      >
                        <Inbox className="w-3.5 h-3.5 mr-2 text-red-400" />
                        <span className="flex-1">Admin Indbakke</span>
                        <Star className="w-2.5 h-2.5 text-red-500 fill-red-500 ml-1" />
                      </div>
                    )}
                  </div>
                )}

                {/* ═══ IKKE LOGGET IND ═══ */}
                {!user && (
                  <div className="border-t border-white/10 p-2">
                    <div className="mb-1.5">
                      <LanguageSelector className="w-full justify-between bg-white/5 rounded-lg text-xs" />
                    </div>
                    <div className="flex gap-2">
                      <button 
                        onClick={() => { navigate('/register'); setMobileMenuOpen(false); }}
                        className="flex-1 h-9 rounded-full text-xs font-semibold bg-emerald-400 text-zinc-900 hover:bg-emerald-300 hover:text-zinc-900 transition-colors flex items-center justify-center gap-1 touch-manipulation"
                        data-testid="mobile-register-btn"
                      >
                        <User className="w-3 h-3" />
                        {t('auth.register')}
                      </button>
                      <button 
                        onClick={() => { navigate('/login'); setMobileMenuOpen(false); }}
                        className="flex-1 h-9 rounded-full text-xs font-medium border border-white/20 text-white/80 hover:bg-white/5 transition-colors touch-manipulation"
                        data-testid="mobile-login-btn"
                      >
                        {t('nav.login')}
                      </button>
                    </div>
                  </div>
                )}

                {/* ═══ KONTO ═══ */}
                {user && (
                  <div className="border-t border-white/10 py-0.5 px-1">
                    <div className="px-3 py-1">
                      <span className="text-zinc-400 text-[9px] font-bold uppercase tracking-wider">Konto</span>
                    </div>
                    <DropdownMenuItem 
                      onSelect={() => { navigate('/farli?settings=true'); setMobileMenuOpen(false); }}
                      className="cursor-pointer py-1.5 px-3 rounded-lg text-white/70 hover:text-white hover:bg-white/5 text-xs touch-manipulation"
                    >
                      <div className="w-4 h-4 rounded-full border border-white/30 overflow-hidden mr-2 flex-shrink-0">
                        <img 
                          src="https://customer-assets.emergentagent.com/job_mc-community-1/artifacts/0iwvudcy_905DB496-6C04-454A-A77D-D1FDDD2ED3CA.jpeg" 
                          alt="Farli" className="w-full h-full object-cover"
                        />
                      </div>
                      {t('farli.settings')}
                    </DropdownMenuItem>
                    <DropdownMenuItem 
                      onSelect={() => { navigate('/indstillinger'); setMobileMenuOpen(false); }}
                      className="cursor-pointer py-1.5 px-3 rounded-lg text-white/70 hover:text-white hover:bg-white/5 text-xs touch-manipulation" data-testid="nav-settings-link"
                    >
                      <Settings className="w-3.5 h-3.5 mr-2 text-zinc-400" />
                      {t('settings.title')}
                    </DropdownMenuItem>
                    <DropdownMenuItem 
                      onSelect={() => { navigate('/hjaelp'); setMobileMenuOpen(false); }}
                      className="cursor-pointer py-1.5 px-3 rounded-lg text-white/70 hover:text-white hover:bg-white/5 text-xs touch-manipulation" data-testid="nav-help-link"
                    >
                      <HelpCircle className="w-3.5 h-3.5 mr-2 text-zinc-400" />
                      {t('nav.help')}
                    </DropdownMenuItem>
                    <DropdownMenuItem 
                      onSelect={() => { handleLogout(); setMobileMenuOpen(false); }}
                      className="cursor-pointer py-1.5 px-3 rounded-lg text-red-400 hover:bg-red-500/10 text-xs touch-manipulation"
                    >
                      <LogOut className="w-3.5 h-3.5 mr-2" />
                      {t('nav.logout')}
                    </DropdownMenuItem>
                  </div>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </div>
      {/* Global invite dialog — driven by the "Inviter venner" hamburger item */}
      <InviteDialog open={inviteOpen} onClose={() => setInviteOpen(false)} />
      {/* ProfileWizard — driven by hamburger "Andet" valg */}
      <ProfileWizard
        open={showProfileWizard}
        onClose={() => setShowProfileWizard(false)}
        onCreateClub={() => { setShowProfileWizard(false); navigate('/register?type=club'); }}
      />
    </nav>
  );
};

export default Navbar;
